import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./board-date-picker.scss";

interface BoardDatePickerProps {
  value: string | null; // ISO yyyy-mm-dd
  onChange: (next: string | null) => void;
  placeholder?: string;
}

const WEEKDAYS: string[] = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// A shadcn-style date picker: a radix Popover (the primitive) wrapping a
// dayjs-driven month calendar — no plain <input type="date">.
const BoardDatePicker: React.FC<BoardDatePickerProps> = ({ value, onChange, placeholder = "No date" }) => {
  const [open, setOpen] = useState<boolean>(false);
  const selected: Dayjs | null = value ? dayjs(value) : null;
  const [viewMonth, setViewMonth] = useState<Dayjs>(() => (selected ?? dayjs()).startOf("month"));

  const today: Dayjs = dayjs();
  const gridStart: Dayjs = viewMonth.startOf("month").startOf("week");
  const days: Dayjs[] = Array.from({ length: 42 }, (_unused: unknown, index: number) => gridStart.add(index, "day"));

  const commit = (next: string | null): void => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next: boolean) => {
        setOpen(next);
        if (next) setViewMonth((selected ?? dayjs()).startOf("month"));
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className={classNames("kuika-datepicker__trigger", { "kuika-datepicker__trigger--empty": !selected })}
        >
          <KuikaSvg name="calendar-event" width={14} height={14} />
          <span className="kuika-datepicker__value">{selected ? selected.format("MMM D, YYYY") : placeholder}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="kuika-datepicker__pop" align="end" sideOffset={6}>
          <div className="kuika-datepicker__header">
            <button
              type="button"
              className="kuika-datepicker__nav"
              aria-label="Previous month"
              onClick={() => setViewMonth((month: Dayjs) => month.subtract(1, "month"))}
            >
              ‹
            </button>
            <span className="kuika-datepicker__month">{viewMonth.format("MMMM YYYY")}</span>
            <button
              type="button"
              className="kuika-datepicker__nav"
              aria-label="Next month"
              onClick={() => setViewMonth((month: Dayjs) => month.add(1, "month"))}
            >
              ›
            </button>
          </div>
          <div className="kuika-datepicker__weekdays">
            {WEEKDAYS.map((weekday: string) => (
              <span key={weekday} className="kuika-datepicker__weekday">
                {weekday}
              </span>
            ))}
          </div>
          <div className="kuika-datepicker__grid">
            {days.map((day: Dayjs) => (
              <button
                key={day.format("YYYY-MM-DD")}
                type="button"
                className={classNames("kuika-datepicker__day", {
                  "kuika-datepicker__day--muted": day.month() !== viewMonth.month(),
                  "kuika-datepicker__day--today": day.isSame(today, "day"),
                  "kuika-datepicker__day--selected": selected !== null && day.isSame(selected, "day")
                })}
                onClick={() => commit(day.format("YYYY-MM-DD"))}
              >
                {day.date()}
              </button>
            ))}
          </div>
          <div className="kuika-datepicker__footer">
            <button
              type="button"
              className="kuika-datepicker__action"
              onClick={() => commit(dayjs().format("YYYY-MM-DD"))}
            >
              Today
            </button>
            {selected !== null && (
              <button type="button" className="kuika-datepicker__action" onClick={() => commit(null)}>
                Clear
              </button>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default BoardDatePicker;
